"use client";

import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import { motion } from "motion/react";
import { Check, X, Zap, Scissors, Film, Bot, Shield, Clock } from "lucide-react";

const advIcons = [Zap, Scissors, Film, Bot, Clock, Shield];
const advKeys = ["comp.adv1", "comp.adv2", "comp.adv3", "comp.adv5", "comp.adv7", "comp.adv8"];
const sharedKeys = ["comp.shared1", "comp.shared2", "comp.shared3", "comp.shared4", "comp.shared5", "comp.shared6"];

export function Comparison() {
  const { T } = useLang();
  const U = useUpper();

  return (
    <section id="karsilastir" className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30 mb-4"
          >
            {U(T("comp.badge"))}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white">
            {T("comp.title1")}{" "}
            <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
              {T("comp.title2")}
            </span>
          </h2>
          <p className="text-neutral-600 dark:text-slate-300 mt-4 max-w-2xl mx-auto text-lg">
            {T("comp.desc")}
          </p>
        </motion.div>

        {/* Exclusive Advantages Grid */}
        <div className="mb-16">
          <div className="flex flex-col items-center gap-3 mb-8">
            <div className="flex items-center gap-3 w-full">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-cyan-200 dark:to-cyan-400/30" />
              <span className="text-xs font-black tracking-widest text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30 whitespace-nowrap">
                {T("comp.only_badge")}
              </span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-cyan-200 dark:to-cyan-400/30" />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { emoji: "🎙️", label: "Advanced AI Voices" },
                { emoji: "🎬", label: "Professional Studio Tools" },
                { emoji: "✍️", label: "AI Writing Assistant" },
              ].map((chip) => (
                <span
                  key={chip.label}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-600 dark:text-slate-300 bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/10 px-3 py-1 rounded-full"
                >
                  <span>{chip.emoji}</span>
                  {chip.label}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {advKeys.map((key, i) => {
              const Icon = advIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.5, type: "spring", stiffness: 100 }}
                  whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                  className="group relative bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10 rounded-2xl p-6 hover:border-cyan-200 dark:hover:border-cyan-400/40 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300"
                >
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-500/15 dark:to-teal-500/10 border border-cyan-100 dark:border-cyan-400/30 flex items-center justify-center flex-shrink-0 group-hover:from-cyan-100 group-hover:to-teal-100 dark:group-hover:from-cyan-500/25 dark:group-hover:to-teal-500/20 transition-colors">
                      <Icon size={20} className="text-cyan-600 dark:text-cyan-300" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-neutral-900 dark:text-white mb-1">{T(`${key}.title`)}</h3>
                      <p className="text-sm text-neutral-600 dark:text-slate-300 leading-relaxed mb-3">{T(`${key}.desc`)}</p>
                      <div className="flex items-start gap-4 pt-3 border-t border-neutral-100 dark:border-white/5">
                        <div className="flex items-center gap-1.5 text-xs">
                          <div className="w-4 h-4 rounded-full bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center">
                            <Check size={10} className="text-cyan-600 dark:text-cyan-300" />
                          </div>
                          <span className="font-semibold text-cyan-600 dark:text-cyan-300">SpeakRoar</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-neutral-400 dark:text-slate-500">
                          <div className="w-4 h-4 rounded-full bg-red-50 dark:bg-red-500/15 flex items-center justify-center">
                            <X size={10} className="text-red-400 dark:text-red-300" />
                          </div>
                          <span>{T(`${key}.competitor`)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Shared Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-neutral-200 dark:to-white/10" />
            <span className="text-xs font-bold text-neutral-400 dark:text-slate-500 uppercase tracking-widest">
              {T("comp.shared_badge")}
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-neutral-200 dark:to-white/10" />
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {sharedKeys.map((key, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-neutral-50 dark:bg-slate-900/50 border border-neutral-200 dark:border-white/10 rounded-full text-sm text-neutral-600 dark:text-slate-200 font-medium">
                <Check size={14} className="text-neutral-400 dark:text-slate-500" />
                {T(key)}
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
}
